import { CONTENT_TYPE, State } from "../enums";
import { isYleEmbed } from "../helpers/commonUtils";
import { getJson } from "../helpers/request";
import { tunnusExists } from "../hooks/useLoginData/useLoginData";
import { GeolocationData, PlayerEmbedParams } from "../types/common";
import { Media } from "../types/player-api";

export type PreviewResponse = Record<State, Media>;

export interface MediaData {
  state: State;
  data: Media;
}

const isSSL = window.location.protocol === "https:";

const getMediaData = async ({
  geolocation,
  id,
  isMobile,
  language
}: {
  geolocation: GeolocationData;
  id?: string;
  isMobile: boolean;
  language?: string;
}): Promise<MediaData> => {
  const requiredParameters = {
    countryCode: geolocation.countryCode,
    host: window.location.host,
    isMobile,
    isPortabilityRegion: geolocation.isPortabilityRegion,
    ssl: isSSL
  };

  const parameters = language
    ? { language, ...requiredParameters }
    : requiredParameters;
  const { data } = await getJson<{
    data: PreviewResponse;
  }>({
    parameters,
    url: `${__PLAYER_API}/v1/preview/${id}.json`,
    withCredentials: tunnusExists() || isYleEmbed()
  });

  const validStates: string[] = Object.values(State);
  const stateString = Object.keys(data).find(key => validStates.includes(key));

  if (!stateString) {
    console.error(
      "%cgetMediaData() did not find a valid state in player-api response.",
      "font-size:18px;"
    );
    throw Error();
  }

  const res = data[stateString];
  if (res.service_schedule) {
    res.service_schedule = res.service_schedule.map(schedule => ({
      ...schedule,
      subtitles: schedule.subtitles.map(s => ({
        kind: s.kind,
        label: s.trackKey,
        language: s.language
      }))
    }));
  }

  const {
    audio_tracks,
    auto_subtitles,
    content_rating,
    content_type,
    dvr_window_in_seconds,
    episode_number,
    is_areena_visible,
    is_content_protected,
    ks_token,
    manifest_url,
    media_id,
    media_url,
    next_episode,
    next_ondemand,
    program_id,
    publication_id,
    scrubber_images,
    season,
    service_id,
    service_name,
    service_schedule,
    simulcast_id,
    start_time,
    stream_aspect_ratio,
    ...rest
  } = res;
  const parsedData = {
    audioTracks: audio_tracks,
    autoSubtitles: auto_subtitles,
    contentRating: content_rating,
    contentType: content_type,
    dvrWindowInSeconds: dvr_window_in_seconds,
    episodeNumber: episode_number,
    isAreenaVisible: is_areena_visible,
    isContentProtected: is_content_protected,
    ksToken: ks_token,
    manifestUrl: manifest_url,
    mediaId: media_id,
    mediaUrl: media_url,
    nextEpisode: next_episode,
    nextOndemand: next_ondemand,
    programId: program_id,
    publicationId: publication_id,
    scrubberImages: scrubber_images,
    season: season && {
      id: season.id,
      seasonNumber: season.season_number
    },
    serviceId: service_id,
    serviceName: service_name,
    serviceSchedule: service_schedule,
    simulcastId: simulcast_id,
    startTime: start_time,
    streamAspectRatio: stream_aspect_ratio,
    ...rest
  };

  return { data: parsedData, state: stateString as State };
};

export const getParsedMediaDataForSrcAudio = ({
  embedId,
  src,
  srcDuration,
  srcTitle
}: {
  embedId: string;
  src: string;
  srcDuration?: number;
  srcTitle?: string;
}): MediaData => {
  return {
    data: {
      autoSubtitles: false,
      contentRating: {},
      contentType: CONTENT_TYPE.AUDIO_OBJECT,
      cuepoints: [],
      description: { fin: "Artikkelista generoitu äänitiedosto" },
      duration:
        srcDuration !== undefined && srcDuration > 0
          ? {
              duration_in_seconds: srcDuration,
              h: 0,
              m: 0,
              s: srcDuration
            }
          : undefined,
      image: { id: "" },
      isAreenaVisible: false,
      isContentProtected: false,
      mediaId: embedId,
      //mediaUrl is used for ondemand audio, returns mp3
      mediaUrl: src,
      programId: embedId,
      title: srcTitle ? { fin: srcTitle } : undefined
    },
    state: State.SRC_AUDIO
  };
};

export const fetchMedia = async ({
  attributes,
  embedId,
  geolocation,
  isMobile
}: {
  attributes: PlayerEmbedParams;
  embedId: string;
  geolocation: GeolocationData;
  isMobile: boolean;
}): Promise<MediaData> => {
  const { language, src, srcDuration, srcTitle, yleAudioPlayer } = attributes;

  if (src && yleAudioPlayer) {
    return getParsedMediaDataForSrcAudio({
      embedId,
      src,
      srcDuration,
      srcTitle
    });
  }
  return getMediaData({
    geolocation,
    id: embedId,
    isMobile,
    language
  });
};
